import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  monolithTracePropagationTargets,
  replayPrivacyOptions,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubRecordingEvent,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import { domainMeta } from './domainMeta';

// What Chromium, Gecko and WebKit say when a script declares a global `let`,
// `const` or `class` that an earlier script on the page already declared.
// Chromium prefixes the DOM call that ran the script, such as appendChild.
const REDECLARATION =
  /^(?:Failed to execute '[^']*' on '[^']*': )?(?:Identifier '[^']*' has already been declared|redeclaration of |Can't create duplicate variable)/;

/**
 * Drops a script's redeclaration of a global `let`, `const` or `class`.
 *
 * Conversion trackers remount when the page rerenders them, and the globals an
 * account's tracker declared outlive the mount, so the rerun throws before any
 * of it runs. The bundle cannot raise this at runtime: its modules fail at
 * build time. Every such event names account code this app cannot fix.
 */
export function dropAccountScriptRedeclarationError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.type === 'SyntaxError' &&
    REDECLARATION.test(exception.value ?? '')
  ) {
    return null;
  }

  return event;
}

/**
 * Browser error reporting, tracing, replay, profiling and logs for the public
 * jobs directory.
 *
 * Dormant unless SENTRY_DSN is baked into the build. That gate is the whole
 * switch: a local `npm run start` and a Jest run both leave it unset and
 * therefore start no client at all.
 *
 * One bundle serves every tenant, so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load.
 */

export function initSentry(): void {
  const dsn = process.env.SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name.
    release: process.env.COMMIT_SHA,
    // The directory is the widest surface Fountain serves: every job seeker
    // who follows a link lands here, on whatever browser they own. One page
    // load in a hundred is enough to see how it performs.
    tracesSampleRate: sampleRate(process.env.SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(process.env.SENTRY_PROFILES_SAMPLE_RATE, 1),
    // Nothing is recorded routinely. A session that produces an error is
    // recorded whatever this is, which is the case worth watching back.
    replaysSessionSampleRate: sampleRate(
      process.env.SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0,
    ),
    replaysOnErrorSampleRate: sampleRate(
      process.env.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1,
    ),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: process.env.SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Nothing here is a job seeker's own data, but the search box and the
      // location field are theirs. Masked by default; a component opts its own
      // subtree out.
      Sentry.replayIntegration({
        // Scrubs the navigation and network entries the recording holds.
        //
        // Known limit, accepted: the page URL, query values such as search
        // terms included, reaches the replay verbatim through rrweb's meta
        // event, which passes no SDK hook. So does the `href` of a link with
        // no query string. On-page text stays masked.
        beforeAddRecordingEvent: scrubRecordingEvent,
        ...replayPrivacyOptions,
      }),
      // Fed by `utils/sentryFlags`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // The directory is unauthenticated and anonymous. Nothing identifies a
    // visitor, here or anywhere else in this app.
    sendDefaultPii: false,
    ignoreErrors: [
      // WebKit rejects a Safari web extension's message from a tab it cannot
      // find. This app calls no extension API.
      /^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/,
    ],
    beforeSend: dropAccountScriptRedeclarationError,
  });

  Sentry.setTag('tenant', domainMeta.tenant);
  // A random id per page load, so an issue counts the people it reaches and a
  // users-affected alert can fire. It is kept in memory only and names no
  // account, applicant or browser.
  Sentry.setUser({ id: Math.random().toString(36).slice(2) });
}
